/**
 * useKeyboardShortcuts
 *
 * Registers global keyboard shortcuts for the Kantera web dashboard.
 * Shortcuts fire only when no text input, textarea, or select element has
 * focus — prevents interference with form interactions.
 *
 * Registered shortcuts:
 *   Ctrl+K / Cmd+K  — Focus the global search input (if present) or navigate
 *                     to /players.
 *   Ctrl+N / Cmd+N  — Navigate to create player (/players/new).
 *   Escape          — Close the topmost open modal (dispatches a custom
 *                     'kantera:close-modal' event that modals can subscribe to).
 *
 * Usage:
 *   // In App.tsx or main layout component
 *   import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
 *   useKeyboardShortcuts();
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/** Event name used by modals to listen for Escape close requests. */
export const CLOSE_MODAL_EVENT = 'kantera:close-modal';

/** Returns true when the focus target is an interactive text element. */
function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;

      // Never fire shortcuts while the user is typing
      if (isTypingTarget(document.activeElement)) {
        // Escape is still handled even in inputs (closes modals)
        if (e.key !== 'Escape') return;
      }

      // Ctrl+K / Cmd+K — focus search or navigate to players
      if (isMeta && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[type="search"], input[placeholder*="Search"], input[placeholder*="search"]',
        );
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        } else {
          navigate('/players');
        }
        return;
      }

      // Ctrl+N / Cmd+N — navigate to create player
      if (isMeta && e.key === 'n') {
        e.preventDefault();
        navigate('/players/new');
        return;
      }

      // Escape — close topmost open modal
      if (e.key === 'Escape') {
        window.dispatchEvent(new CustomEvent(CLOSE_MODAL_EVENT));
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);
}
